import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button } from "@mui/material";
import type { ProductVariant } from "../types";
import logo from "../logoImages/logo-wordmark.png";

const BASE_URL = process.env.REACT_APP_API_URL ?? "";
const SLIDE_INTERVAL = 6000;

type Slide = "logo" | "arrivals";

const HeroBanner = () => {
  const navigate = useNavigate();
  const [slide, setSlide] = useState<Slide>("logo");
  const [newArrivals, setNewArrivals] = useState<ProductVariant[]>([]);

  useEffect(() => {
    fetch(`${BASE_URL}/api/v1/products?category=New+Arrivals`)
      .then((r) => r.json())
      .then((data: ProductVariant[]) => setNewArrivals(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(
      () => setSlide((s) => (s === "logo" ? "arrivals" : "logo")),
      SLIDE_INTERVAL
    );
    return () => clearInterval(t);
  }, []);

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        height: { xs: "44vh", md: "38vh" },
        minHeight: 280,
        maxHeight: 480,
        overflow: "hidden",
      }}
    >
      {/* ── LOGO SLIDE ── dark, centered, typographic */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          bgcolor: "#0a0a0a",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0,
          transition: "opacity 0.8s ease",
          opacity: slide === "logo" ? 1 : 0,
          pointerEvents: slide === "logo" ? "auto" : "none",
        }}
      >
        {/* top rule */}
        <Box
          sx={{
            width: { xs: 200, md: 360 },
            height: "1px",
            bgcolor: "#2a2a2a",
            mb: 3,
          }}
        />

        <Typography
          sx={{
            color: "#1c1c1c",
            fontSize: { xs: "0.55rem", md: "0.6rem" },
            letterSpacing: "0.5em",
            textTransform: "uppercase",
            mb: 2,
          }}
        >
          Est. 2026
        </Typography>

        {/* logo lockup */}
        <Box
          component="img"
          src={logo}
          alt="Shop with Lemanade"
          sx={{
            width: { xs: 260, md: 420 },
            height: "auto",
          }}
        />

        <Typography
          sx={{
            color: "#2a2a2a",
            fontSize: { xs: "0.55rem", md: "0.6rem" },
            letterSpacing: "0.4em",
            textTransform: "uppercase",
            mt: 2,
            mb: 3,
          }}
        >
          The New Standard
        </Typography>

        {/* bottom rule */}
        <Box
          sx={{
            width: { xs: 180, md: 280 },
            height: "1px",
            bgcolor: "#2a2a2a",
          }}
        />
      </Box>

      {/* ── NEW ARRIVALS SLIDE ── dark, split layout */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          bgcolor: "#0a0a0a",
          transition: "opacity 0.8s ease",
          opacity: slide === "arrivals" ? 1 : 0,
          pointerEvents: slide === "arrivals" ? "auto" : "none",
          display: "flex",
          alignItems: "stretch",
        }}
      >
        {/* left: heading + CTA */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: { xs: 4, md: 7 },
            borderRight: "1px solid #1e1e1e",
            flexShrink: 0,
            width: { xs: "46%", md: "36%" },
          }}
        >
          <Typography
            sx={{
              color: "#2a2a2a",
              fontSize: "0.6rem",
              letterSpacing: "0.35em",
              textTransform: "uppercase",
              mb: 1.5,
            }}
          >
            Just Dropped
          </Typography>

          <Typography
            sx={{
              color: "white",
              fontWeight: 800,
              textTransform: "uppercase",
              fontSize: { xs: "1.5rem", md: "2.4rem" },
              lineHeight: 1.05,
              letterSpacing: "-0.01em",
              mb: 3,
            }}
          >
            New
            <br />
            Arrivals
          </Typography>

          <Button
            onClick={() => navigate("/category/new-arrivals")}
            variant="outlined"
            disableElevation
            sx={{
              alignSelf: "flex-start",
              borderColor: "#2a2a2a",
              color: "#737373",
              px: 3,
              py: 1,
              fontSize: "0.65rem",
              letterSpacing: "0.12em",
              borderRadius: 0,
              "&:hover": {
                borderColor: "white",
                color: "white",
                bgcolor: "transparent",
              },
            }}
          >
            View All
          </Button>
        </Box>

        {/* right: product list */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            px: { xs: 3, md: 6 },
            overflowX: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {newArrivals.length > 0 ? (
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              {newArrivals.slice(0, 5).map((p, i) => (
                <Box
                  key={p.id}
                  onClick={() => navigate("/category/new-arrivals")}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 1.25,
                    borderBottom:
                      i < Math.min(newArrivals.length, 5) - 1
                        ? "1px solid #1a1a1a"
                        : "none",
                    cursor: "pointer",
                    "&:hover .product-name": { color: "#a3a3a3" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#2a2a2a",
                        fontSize: "0.6rem",
                        fontWeight: 600,
                        width: 18,
                        flexShrink: 0,
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </Typography>
                    <Typography
                      className="product-name"
                      sx={{
                        color: "#737373",
                        fontSize: { xs: "0.72rem", md: "0.82rem" },
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        transition: "color 0.15s",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {p.name}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      color: "#2a2a2a",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      flexShrink: 0,
                      ml: 2,
                    }}
                  >
                    {p.salePrice != null ? `$${p.salePrice}` : "—"}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography
              sx={{
                color: "#2a2a2a",
                fontSize: "0.8rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              New drops coming soon
            </Typography>
          )}
        </Box>
      </Box>

      {/* ── Slide dots ── */}
      <Box
        sx={{
          position: "absolute",
          bottom: 14,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: 0.75,
          zIndex: 2,
        }}
      >
        {(["logo", "arrivals"] as Slide[]).map((s) => (
          <Box
            key={s}
            onClick={() => setSlide(s)}
            sx={{
              width: slide === s ? 20 : 5,
              height: 5,
              borderRadius: 3,
              bgcolor: slide === s ? "white" : "#333",
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default HeroBanner;
